import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppLayout } from "../components/tabon/AppLayout";
import { api, formatError } from "../lib/api";
import { ChevronLeft, Search, Save } from "lucide-react";

// Menu → Availability: turn items off per order type in bulk (AA-4) — mark thirty items
// dine-in-only in one Save, instead of opening each. Each channel is a toggle; OFF puts the
// channel's service-point id in the item's unavailable_at. Area/table ids (the location axis) in
// unavailable_at are preserved untouched — this screen only moves the channel (order-type) ids.
const key = (arr) => (arr || []).slice().sort().join(",");

export default function ItemAvailability() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [channels, setChannels] = useState([]);
  const [avail, setAvail] = useState({});   // item_id -> unavailable_at list (full, incl. non-channel ids)
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [i, c, sp] = await Promise.all([api.get("/menu/items"), api.get("/menu/categories"), api.get("/service-points")]);
      const its = i.data || [];
      setItems(its);
      setCats(c.data || []);
      setChannels(((sp.data && sp.data.service_points) || []).filter(x => x.level === "channel" && x.active !== false));
      setAvail(Object.fromEntries(its.map(it => [it.id, Array.isArray(it.unavailable_at) ? it.unavailable_at : []])));
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const catName = (id) => (cats.find(c => c.id === id) || {}).name || "—";
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it => (it.name || "").toLowerCase().includes(q) || catName(it.category_id).toLowerCase().includes(q));
  }, [items, search, cats]);

  const orig = useMemo(() => Object.fromEntries(items.map(it => [it.id, Array.isArray(it.unavailable_at) ? it.unavailable_at : []])), [items]);
  const dirty = useMemo(() => items.some(it => key(avail[it.id]) !== key(orig[it.id])), [items, avail, orig]);

  const toggle = (itemId, chId) => setAvail(p => {
    const cur = p[itemId] || [];
    return { ...p, [itemId]: cur.includes(chId) ? cur.filter(x => x !== chId) : [...cur, chId] };
  });
  const setAllShown = (chId, off) => setAvail(p => {
    const n = { ...p };
    visible.forEach(it => {
      const cur = n[it.id] || [];
      n[it.id] = off ? (cur.includes(chId) ? cur : [...cur, chId]) : cur.filter(x => x !== chId);
    });
    return n;
  });

  const save = async () => {
    const changed = items.filter(it => key(avail[it.id]) !== key(orig[it.id]))
      .map(it => ({ item_id: it.id, unavailable_at: avail[it.id] || [] }));
    if (!changed.length) { toast.info("No changes to save"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/menu/item-availability", { items: changed });
      toast.success(`Saved ${data.updated} item${data.updated === 1 ? "" : "s"}`);
      await load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  return (
    <AppLayout>
      <div className="max-w-[900px] mx-auto px-4 md:px-6 py-6">
        <button onClick={() => navigate("/menu")} className="flex items-center gap-1 text-sm text-[#A0A0A0] hover:text-white mb-4">
          <ChevronLeft size={16} /> Back to menu
        </button>

        <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
          <div>
            <h1 className="text-xl font-black text-white">Order types</h1>
            <p className="text-xs text-[#A0A0A0] mt-1">Turn an item off for an order type — a dine-in-only dish, or one not offered for takeaway. Green = available; tap to exclude.</p>
          </div>
          <button onClick={save} disabled={saving || !dirty}
            className="flex items-center gap-1.5 bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-40 text-white text-sm font-bold rounded-lg px-4 py-2.5">
            <Save size={15} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>

        <div className="relative mb-3">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A0A0]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search items or a category…"
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-[#6B6B6B] focus:border-[#9D00FF] focus:outline-none" />
        </div>

        {channels.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mb-3 text-xs text-[#A0A0A0]">
            <span>For all {visible.length} shown:</span>
            {channels.map(ch => (
              <span key={ch.id} className="inline-flex items-center gap-1">
                <button type="button" onClick={() => setAllShown(ch.id, true)} className="px-2 py-1 rounded-lg border border-white/10 hover:border-red-400 hover:text-red-300">{ch.label} off</button>
                <button type="button" onClick={() => setAllShown(ch.id, false)} className="px-2 py-1 rounded-lg border border-white/10 hover:border-[#9D00FF] hover:text-white">on</button>
              </span>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-sm text-[#A0A0A0] py-10">Loading…</div>
        ) : channels.length === 0 ? (
          <div className="text-sm text-[#555] py-10">No order-type channels configured.</div>
        ) : (
          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-[#A0A0A0] border-b border-white/10">
              <span className="flex-1">Item</span><span>Order types</span>
            </div>
            <div className="max-h-[62vh] overflow-y-auto">
              {visible.map((it) => {
                const ua = avail[it.id] || [];
                return (
                  <div key={it.id} className="flex items-center gap-3 px-4 py-2 border-b border-white/5">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white truncate">{it.name}</div>
                      <div className="text-[11px] text-[#A0A0A0] truncate">{catName(it.category_id)}</div>
                    </div>
                    <div className="flex gap-1.5 flex-wrap justify-end shrink-0">
                      {channels.map((ch) => {
                        const off = ua.includes(ch.id);
                        return (
                          <button key={ch.id} type="button" onClick={() => toggle(it.id, ch.id)}
                            aria-pressed={!off}
                            className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition ${off ? "border-white/10 text-[#777] line-through hover:border-white/30" : "bg-[#9D00FF]/20 border-[#9D00FF] text-white"}`}>
                            {ch.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {visible.length === 0 && <div className="text-sm text-[#555] px-4 py-8">No items match.</div>}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
